import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { UserDashboardService } from '../services/user-dashboard.service';
import { Router } from '@angular/router';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { USER_WORKFLOW_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';

@Component({
  selector: 'app-manage-approval',
  standalone: true,
  imports: [CommonModule, MatIconModule, TutorialVideoWidgetComponent],
  templateUrl: './manage-approval.component.html',
  styleUrl: './manage-approval.component.css'
})

export class ManageApprovalComponent implements OnInit {
  readonly userWorkflowTutorialSequence: TutorialVideoStep[] = USER_WORKFLOW_TUTORIAL_FLOW;
  projects: any[] = [];
  isLoading = true;
  constructor(private readonly userDashboardService: UserDashboardService, private readonly router: Router,
    private readonly cdr: ChangeDetectorRef) { }

  managerApprove(project: any): void {
    project.status = 'Pending RAES Review';
  }
  adminMarkCompleted(project: any): void {
    project.status = 'Completed';
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userDashboardService.getUserDashboardData(true).subscribe({
      next: (response: any) => {
        console.info("Dashboard data fetched successfully", response);
        this.projects = (response.payload || []).slice().sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
          const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
          return dateB - dateA;
        });
        this.isLoading = false;
      },
      error: (error: any) => {
        console.trace("Error loading dashboard data", error);
        this.projects = [];
        this.isLoading = false;
      }
    })
  }

  viewProject(projectId: number, departmentId = ''): void {
    const queryParams: Record<string, string> = {};
    if (departmentId) {
      this.router.navigate([
        '/user-dashboard/project',
        projectId,
        { departmentId: departmentId }
      ], {
        queryParams: { fromManageApproval: 'true' }
      });
    } else {
      this.router.navigate([
        '/user-dashboard/project',
        projectId
      ], {
        queryParams: { fromManageApproval: 'true' }
      });
    }
    queryParams.fromManageApproval = 'true';
  }

    getDueProgress(project: any): number {
    const daysRemaining = this.getDaysRemaining(project.due_date);

    if (daysRemaining <= 0) {
      return 100;
    }

    const progress = ((30 - daysRemaining) / 30) * 100;
    return Math.max(0, Math.min(100, Math.round(progress)));
  }

  getDueState(project: any): 'normal' | 'near' | 'overdue' {
    const daysRemaining = this.getDaysRemaining(project.due_date);

    if (daysRemaining < 0) {
      return 'overdue';
    }

    if (daysRemaining <= 3) {
      return 'near';
    }

    return 'normal';
  }

  getDueLabel(project: any): string {
    const daysRemaining = this.getDaysRemaining(project.due_date);

    if (daysRemaining < 0) {
      const overdueDays = Math.abs(daysRemaining);
      return `Overdue by ${overdueDays} day${overdueDays === 1 ? '' : 's'}`;
    }

    if (daysRemaining === 0) {
      return 'Due today';
    }

    return `Due in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
  }

  //   private isOverdue(project: any): boolean {
  //   return new Date(project.dueDate).getTime() < Date.now() && project.status !== 'Completed';
  // }

  parseDateLocal(dateStr: string): Date | null {
    if (!dateStr) {
      return null;
    }
    const datePart = dateStr.split('T')[0].split(' ')[0];
    if (datePart.includes('-')) {
      const [year, month, day] = datePart.split('-').map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    } else if (datePart.includes('/')) {
      const [month, day, year] = datePart.split('/').map(Number);
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    }
    return null;
  }

  formatDate(dateStr: string): string {
    if (!dateStr) {
      return 'N/A';
    }
    const d = this.parseDateLocal(dateStr);
    if (!d || Number.isNaN(d.getTime())) {
      return dateStr;
    }
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dd = String(d.getDate()).padStart(2, '0');
    return `${months[d.getMonth()]} ${dd}, ${d.getFullYear()}`;
  }

  getStatusBadgeClass(status: string): string {
    const normalizedStatus = String(status || '').toLowerCase().trim().replaceAll(/\s+/g, ' ');
    switch (normalizedStatus) {
      case 'completed': return 'badge-success';
      case 'submitted':
      case 'in progress': return 'badge-progress';
      case 'pending rre review':
      case 'pending raes review': return 'badge-danger';
      case 'assigned': return 'badge-assigned';
      case 'unassigned': return 'badge-unassigned';
      case 'deleted': return 'badge-deleted';
      case 'overdue': return 'badge-overdue';
      default: return 'badge-secondary';
    }
  }

  private getDaysRemaining(dueDate: string): number {
    const dueDateOnly = this.parseDateLocal(dueDate);
    if (!dueDateOnly || Number.isNaN(dueDateOnly.getTime())) {
      return Number.NaN;
    }
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((dueDateOnly.getTime() - todayOnly.getTime()) / millisecondsPerDay);
  }

  getAssignmentLabel(project: any): string {
    return project.assigned_person_name ? 'Assigned To' : 'Assignment';
  }

  getAssignedName(project: any): string {
    return project.assigned_person_name || 'Unassigned';
  }

  getSubCollection(subCollection: any): string {
    const values = Array.isArray(subCollection) ? subCollection : [subCollection];
    const names = values
      .map((item: any) => typeof item === 'string' ? item : item?.sub_collection_name)
      .filter((name: string) => !!name?.trim());

    return names.length > 0 ? names.join(', ') : 'N/A';
  }

}
